import React, { useState, useCallback, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
  Chip,
  Divider,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  section: { marginTop: theme.spacing(4) },
  sectionTitle: { marginBottom: theme.spacing(1.5), fontWeight: 600 },
  formRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
    alignItems: "flex-end",
  },
  paper: { padding: theme.spacing(2) },
}));

const MAX_CHARS = 200;

const SuperAdminAnnouncements = () => {
  const classes = useStyles();
  const [companies, setCompanies] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [annForm, setAnnForm] = useState({ message: "", companyId: "", expiresAt: "", durationSec: "" });
  const [bannerMessages, setBannerMessages] = useState([]);
  const [bannerForm, setBannerForm] = useState({ message: "", type: "tip", companyId: "", durationSec: "" });

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/superadmin/dashboard");
      setCompanies(data.companies || []);
    } catch (_) {}
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const { data } = await api.get("/superadmin/announcements");
      setAnnouncements(data);
    } catch (_) {}
  }, []);

  const fetchBannerMessages = useCallback(async () => {
    try {
      const { data } = await api.get("/superadmin/banner-messages");
      setBannerMessages(data);
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchCompanies();
    fetchAnnouncements();
    fetchBannerMessages();
  }, [fetchCompanies, fetchAnnouncements, fetchBannerMessages]);

  const handleCreateAnnouncement = async () => {
    if (!annForm.message.trim()) return;
    try {
      await api.post("/announcements", {
        message: annForm.message,
        companyId: annForm.companyId || undefined,
        expiresAt: annForm.expiresAt || undefined,
        durationSec: annForm.durationSec !== "" ? Number(annForm.durationSec) : null,
      });
      toast.success("Aviso creado");
      setAnnForm({ message: "", companyId: "", expiresAt: "", durationSec: "" });
      fetchAnnouncements();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Aviso eliminado");
      fetchAnnouncements();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCreateBanner = async () => {
    if (!bannerForm.message.trim()) return;
    try {
      await api.post("/banner-messages", {
        message: bannerForm.message,
        type: bannerForm.type,
        companyId: bannerForm.companyId || undefined,
        durationSec: bannerForm.durationSec !== "" ? Number(bannerForm.durationSec) : null,
      });
      toast.success("Mensaje de banner creado");
      setBannerForm({ message: "", type: "tip", companyId: "", durationSec: "" });
      fetchBannerMessages();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteBanner = async (id) => {
    try {
      await api.delete(`/banner-messages/${id}`);
      toast.success("Mensaje eliminado");
      fetchBannerMessages();
    } catch (err) {
      toastError(err);
    }
  };

  const companyName = (companyId) =>
    companyId
      ? companies.find((c) => c.id === companyId)?.name || `ID ${companyId}`
      : "Todas";

  const formatDuration = (durationSec) => {
    if (durationSec == null) return "6s (def)";
    if (durationSec === 0) return "Estático";
    return `${durationSec}s`;
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>Avisos y Banners</Typography>

      {/* ── Announcements ── */}
      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>Avisos</Typography>
        <Paper className={classes.paper}>
          <div className={classes.formRow}>
            <div style={{ flex: 2, minWidth: 200 }}>
              <TextField
                label={`Mensaje del aviso (máx. ${MAX_CHARS})`}
                value={annForm.message}
                onChange={e => {
                  if (e.target.value.length <= MAX_CHARS)
                    setAnnForm({ ...annForm, message: e.target.value });
                }}
                fullWidth
                size="small"
                helperText={`${annForm.message.length}/${MAX_CHARS}`}
              />
            </div>
            <FormControl size="small" style={{ flex: 1, minWidth: 160 }}>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={annForm.companyId}
                onChange={e => setAnnForm({ ...annForm, companyId: e.target.value })}
                label="Empresa"
              >
                <MenuItem value="">Todas las empresas</MenuItem>
                {companies.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Expira (fecha)"
              type="datetime-local"
              value={annForm.expiresAt}
              onChange={e => setAnnForm({ ...annForm, expiresAt: e.target.value })}
              style={{ flex: 1, minWidth: 180 }}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Duración (seg)"
              type="number"
              inputProps={{ min: 0 }}
              value={annForm.durationSec}
              onChange={e => setAnnForm({ ...annForm, durationSec: e.target.value })}
              style={{ width: 130 }}
              size="small"
              helperText="Vacío=6s · 0=fijo"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateAnnouncement}
              disabled={!annForm.message.trim()}
            >
              Publicar
            </Button>
          </div>

          <Divider style={{ marginBottom: 12 }} />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mensaje</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Expira</TableCell>
                <TableCell>Duración</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {announcements.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="textSecondary">Sin avisos activos</Typography>
                  </TableCell>
                </TableRow>
              )}
              {announcements.map(a => (
                <TableRow key={a.id}>
                  <TableCell style={{ maxWidth: 400, wordBreak: "break-word" }}>{a.message}</TableCell>
                  <TableCell>{companyName(a.companyId)}</TableCell>
                  <TableCell>
                    {a.expiresAt ? format(parseISO(a.expiresAt), "dd/MM/yy HH:mm") : "Sin vencimiento"}
                  </TableCell>
                  <TableCell>{formatDuration(a.durationSec)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleDeleteAnnouncement(a.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </div>

      {/* ── Banner Messages ── */}
      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>Mensajes del Banner</Typography>
        <Paper className={classes.paper}>
          <div className={classes.formRow}>
            <div style={{ flex: 2, minWidth: 200 }}>
              <TextField
                label={`Mensaje (máx. ${MAX_CHARS})`}
                value={bannerForm.message}
                onChange={e => {
                  if (e.target.value.length <= MAX_CHARS)
                    setBannerForm({ ...bannerForm, message: e.target.value });
                }}
                fullWidth
                size="small"
                helperText={`${bannerForm.message.length}/${MAX_CHARS}`}
              />
            </div>
            <FormControl size="small" style={{ minWidth: 120 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={bannerForm.type}
                onChange={e => setBannerForm({ ...bannerForm, type: e.target.value })}
                label="Tipo"
              >
                <MenuItem value="tip">Tip</MenuItem>
                <MenuItem value="marketing">Marketing</MenuItem>
                <MenuItem value="greeting">Saludo</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" style={{ minWidth: 160 }}>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={bannerForm.companyId}
                onChange={e => setBannerForm({ ...bannerForm, companyId: e.target.value })}
                label="Empresa"
              >
                <MenuItem value="">Todas las empresas</MenuItem>
                {companies.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Duración (seg)"
              type="number"
              inputProps={{ min: 0 }}
              value={bannerForm.durationSec}
              onChange={e => setBannerForm({ ...bannerForm, durationSec: e.target.value })}
              style={{ width: 130 }}
              size="small"
              helperText="Vacío=6s · 0=fijo"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateBanner}
              disabled={!bannerForm.message.trim()}
            >
              Agregar
            </Button>
          </div>

          <Divider style={{ marginBottom: 12 }} />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mensaje</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Duración</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bannerMessages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="textSecondary">Sin mensajes</Typography>
                  </TableCell>
                </TableRow>
              )}
              {bannerMessages.map(b => (
                <TableRow key={b.id}>
                  <TableCell style={{ maxWidth: 400, wordBreak: "break-word" }}>{b.message}</TableCell>
                  <TableCell><Chip label={b.type} size="small" /></TableCell>
                  <TableCell>{companyName(b.companyId)}</TableCell>
                  <TableCell>{formatDuration(b.durationSec)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleDeleteBanner(b.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </div>
    </div>
  );
};

export default SuperAdminAnnouncements;
